import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Badge,
  Alert,
  AlertIcon,
  useToast,
  useColorModeValue,
  Icon,
  Divider,
  Heading,
  Spinner,
  Center,
  Code,
  Grid,
} from '@chakra-ui/react';
import { FiPhone, FiCheckCircle, FiXCircle, FiRefreshCw } from 'react-icons/fi';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db, app } from '../../../utils/init-firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

export default function DialpadSettings({ userId, isAdmin = false }) {
  const toast = useToast();
  const [userData, setUserData] = useState(null);
  const [isEnabling, setIsEnabling] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [dialpadUserInfo, setDialpadUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSyncingOptOuts, setIsSyncingOptOuts] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const infoBG = useColorModeValue('blue.50', 'blue.900');
  const successBG = useColorModeValue('green.50', 'green.900');
  const cardBG = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    if (!userId) return;

    const userRef = doc(db, 'users', userId);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserData(data);
        setDialpadUserInfo(data.dialpadUserInfo || null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  const isEnabled = userData?.dialpadEnabled === true;
  const dialpadUserId = userData?.dialpadUserId;
  const userEmail = userData?.Email || userData?.email;
  const dialpadCapable = userData?.dialpadCapable === true;

  const handleToggleDialpad = async (enable) => {
    if (enable) {
      // Enabling Dialpad - need to map user by email
      if (!userEmail) {
        toast({
          title: 'Email required',
          description: 'User must have an email address to enable Dialpad',
          status: 'error',
          duration: 5000,
        });
        return;
      }

      setIsEnabling(true);
      try {
        const functions = getFunctions(app, 'us-east1');
        const mapUserByEmail = httpsCallable(functions, 'mapDialpadUserByEmail');
        
        const result = await mapUserByEmail({ 
          userId,
          email: userEmail 
        });

        if (result.data.success) {
          toast({
            title: 'Dialpad enabled!',
            description: `Linked to Dialpad user: ${result.data.dialpadUserInfo.display_name}`,
            status: 'success',
            duration: 4000,
          });
        } else {
          toast({
            title: 'Could not enable Dialpad',
            description: result.data.error || 'User not found in Dialpad',
            status: 'error',
            duration: 5000,
          });
        }
      } catch (error) {
        console.error('Error enabling Dialpad:', error);
        toast({
          title: 'Error enabling Dialpad',
          description: error.message,
          status: 'error',
          duration: 5000,
        });
      } finally {
        setIsEnabling(false);
      }
    } else {
      // Disabling Dialpad
      if (!window.confirm('Disable Dialpad integration? This will stop syncing calls and messages.')) {
        return;
      }

      setIsDisabling(true);
      try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          dialpadEnabled: false,
          dialpadUserId: null,
          dialpadUserInfo: null,
        });

        toast({
          title: 'Dialpad disabled',
          description: 'Integration has been turned off',
          status: 'success',
          duration: 3000,
        });
      } catch (error) {
        console.error('Error disabling Dialpad:', error);
        toast({
          title: 'Error disabling Dialpad',
          description: error.message,
          status: 'error',
          duration: 5000,
        });
      } finally {
        setIsDisabling(false);
      }
    }
  };

  const handleRefreshMapping = async () => {
    if (!window.confirm('Re-map this user to their Dialpad account? This will verify the connection.')) {
      return;
    }

    setIsEnabling(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const mapUserByEmail = httpsCallable(functions, 'mapDialpadUserByEmail');
      
      const result = await mapUserByEmail({ 
        userId,
        email: userEmail,
        forceRefresh: true,
      });

      if (result.data.success) {
        toast({
          title: 'Mapping refreshed',
          description: 'Dialpad connection verified',
          status: 'success',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error refreshing mapping:', error);
      toast({
        title: 'Error refreshing mapping',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsEnabling(false);
    }
  };

  const handleSyncOptOuts = async () => {
    setIsSyncingOptOuts(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const syncOptOuts = httpsCallable(functions, 'syncDialpadSmsOptOutsNow');
      const result = await syncOptOuts({});
      const s = result.data || {};

      toast({
        title: 'SMS opt-out sync complete',
        description: `${s.optedOut ?? 0} numbers opted out in Dialpad · ${s.contactsFlagged ?? 0} contacts newly marked DNC (SMS) · ${s.contactsCleared ?? 0} cleared · ${s.unmatchedOptedOutNumbers ?? 0} numbers had no matching contact`,
        status: 'success',
        duration: 8000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error syncing SMS opt-outs:', error);
      toast({
        title: 'SMS opt-out sync failed',
        description: error.message,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsSyncingOptOuts(false);
    }
  };

  if (loading) {
    return (
      <Center py={8}>
        <Spinner />
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="md" mb={2}>Dialpad Integration</Heading>
        <Text fontSize="sm" color="gray.600">
          Enable Dialpad to make calls, send SMS, and automatically log communications
        </Text>
      </Box>

      <Divider />

      {/* Not Capable - Show message */}
      {!dialpadCapable && !isAdmin && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="sm" fontWeight="semibold">
              Dialpad Not Available
            </Text>
            <Text fontSize="xs">
              Contact your CRM Admin to request access to Dialpad integration.
            </Text>
          </VStack>
        </Alert>
      )}

      {/* Admin viewing non-capable user */}
      {!dialpadCapable && isAdmin && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="sm" fontWeight="semibold">
              Dialpad Access Disabled
            </Text>
            <Text fontSize="xs">
              This user does not have Dialpad capabilities enabled. Go to Admin Settings → Dialpad tab to grant access.
            </Text>
          </VStack>
        </Alert>
      )}

      {/* User has capability - show normal interface */}
      {dialpadCapable && (
        <>

      {/* Connection Status */}
      <Box 
        p={5} 
        bg={isEnabled ? successBG : infoBG} 
        borderRadius="lg" 
        borderWidth="1px" 
        borderColor={isEnabled ? 'green.200' : borderColor}
      >
        <HStack justify="space-between" mb={3}>
          <HStack spacing={3}>
            <Icon as={FiPhone} boxSize={6} color={isEnabled ? 'green.600' : 'gray.400'} />
            <VStack align="flex-start" spacing={0}>
              <Text fontWeight="semibold">Dialpad Status</Text>
              {userEmail && (
                <Text fontSize="xs" color="gray.600">
                  Email: {userEmail}
                </Text>
              )}
            </VStack>
          </HStack>
          
          {isEnabled ? (
            <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
              <HStack spacing={1}>
                <Icon as={FiCheckCircle} boxSize={3} />
                <Text>Enabled</Text>
              </HStack>
            </Badge>
          ) : (
            <Badge colorScheme="gray" fontSize="sm" px={3} py={1}>
              <HStack spacing={1}>
                <Icon as={FiXCircle} boxSize={3} />
                <Text>Disabled</Text>
              </HStack>
            </Badge>
          )}
        </HStack>

        {isEnabled && dialpadUserInfo && (
          <Box p={3} bg="white" borderRadius="md" borderWidth="1px" borderColor="green.200" mb={3}>
            <Grid templateColumns="repeat(2, 1fr)" gap={3} fontSize="sm">
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color="gray.600">Dialpad Name</Text>
                <Text>{dialpadUserInfo.display_name || 'N/A'}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color="gray.600">Dialpad User ID</Text>
                <Code fontSize="xs">{dialpadUserId || 'N/A'}</Code>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color="gray.600">Dialpad Email</Text>
                <Text fontSize="xs">{dialpadUserInfo.emails?.[0] || 'N/A'}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color="gray.600">Phone Numbers</Text>
                <Text fontSize="xs">{dialpadUserInfo.phone_numbers?.length || 0} numbers</Text>
              </Box>
            </Grid>
          </Box>
        )}

        {isAdmin && (
          <HStack spacing={2}>
            {!isEnabled ? (
              <Button
                colorScheme="blue"
                leftIcon={<FiCheckCircle />}
                onClick={() => handleToggleDialpad(true)}
                isLoading={isEnabling}
                loadingText="Enabling & Mapping..."
                size="sm"
              >
                Enable Dialpad
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  leftIcon={<FiRefreshCw />}
                  variant="outline"
                  onClick={handleRefreshMapping}
                  isLoading={isEnabling}
                >
                  Refresh Mapping
                </Button>
                <Button
                  size="sm"
                  leftIcon={<FiXCircle />}
                  colorScheme="red"
                  variant="outline"
                  onClick={() => handleToggleDialpad(false)}
                  isLoading={isDisabling}
                  loadingText="Disabling..."
                >
                  Disable Dialpad
                </Button>
              </>
            )}
          </HStack>
        )}
      </Box>

      {/* Feature Overview */}
      {isEnabled && (
        <Box>
          <Heading size="sm" mb={3}>Available Features</Heading>
          <VStack align="stretch" spacing={2}>
            <HStack p={3} bg={cardBG} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
              <Icon as={FiPhone} color="blue.500" />
              <Text fontSize="sm">Click-to-call directly from CRM</Text>
            </HStack>
            <HStack p={3} bg={cardBG} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
              <Icon as={FiPhone} color="green.500" />
              <Text fontSize="sm">Send SMS through Dialpad</Text>
            </HStack>
            <HStack p={3} bg={cardBG} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
              <Icon as={FiCheckCircle} color="purple.500" />
              <Text fontSize="sm">Automatic call and SMS logging in deal timeline</Text>
            </HStack>
            <HStack p={3} bg={cardBG} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
              <Icon as={FiCheckCircle} color="orange.500" />
              <Text fontSize="sm">Call transcripts with AI summaries</Text>
            </HStack>
            <HStack p={3} bg={cardBG} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
              <Icon as={FiCheckCircle} color="cyan.500" />
              <Text fontSize="sm">Dialpad analytics integrated into CRM reports</Text>
            </HStack>
          </VStack>
        </Box>
      )}

      {!isEnabled && isAdmin && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="sm" fontWeight="semibold">
              Enable Dialpad Integration
            </Text>
            <Text fontSize="xs">
              When you enable Dialpad, we'll automatically match this user to their Dialpad account
              using their email address ({userEmail || 'no email set'}). Make sure the email matches
              their Dialpad account email exactly.
            </Text>
          </VStack>
        </Alert>
      )}

      {!userEmail && isAdmin && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            This user needs an email address before Dialpad can be enabled.
            Update their profile on the main Admin page.
          </Text>
        </Alert>
      )}

      {/* Help Text */}
      <Box fontSize="xs" color="gray.500" p={4} bg={bgColor} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
        <Text fontWeight="semibold" mb={2}>How It Works</Text>
        <VStack align="flex-start" spacing={1}>
          <Text>
            • We automatically map CRM users to Dialpad accounts using email addresses
          </Text>
          <Text>
            • All calls and messages are logged automatically in the deal timeline
          </Text>
          <Text>
            • Call recordings and transcripts are fetched and summarized with AI
          </Text>
          <Text>
            • Dialpad analytics are integrated into your CRM analytics dashboard
          </Text>
        </VStack>
      </Box>
      </>
      )}

      {/* SMS Opt-Out Sync (company-wide, admin only) */}
      {isAdmin && (
        <Box p={5} bg={cardBG} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <HStack justify="space-between" align="flex-start" spacing={4}>
            <VStack align="flex-start" spacing={1} flex={1}>
              <Text fontWeight="semibold">SMS Opt-Out Sync</Text>
              <Text fontSize="xs" color="gray.500">
                Contacts who reply STOP are marked "Do Not Contact (Texts)" automatically —
                in real time via webhook, plus a full reconciliation against Dialpad's
                company opt-out list every 30 minutes. Use this button to run that
                reconciliation immediately.
              </Text>
            </VStack>
            <Button
              size="sm"
              leftIcon={<FiRefreshCw />}
              colorScheme="blue"
              variant="outline"
              onClick={handleSyncOptOuts}
              isLoading={isSyncingOptOuts}
              loadingText="Syncing..."
              flexShrink={0}
            >
              Sync opt-outs now
            </Button>
          </HStack>
        </Box>
      )}
    </VStack>
  );
}

